import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useAppStore } from '@/lib/store';
import { sendChatMessage, parseToolCalls, crawlUrl, buildSystemPrompt, sendCloudCallMessage, generateCloudImage, analyzeImageViaCloud } from '@/lib/ai/chat';
import { loadWebGPUModel, sendWebGPUMessage, isModelLoaded, isWebGPUSupported, WEBGPU_MODELS } from '@/lib/ai/webgpu';
import { uid, ACCEPTED_EXTENSIONS } from '@/utils/format';
import { isConvexEnabled } from '@/lib/convex';
import { useBrainDoc } from '@hooks/useBrainDoc';
import FallbackNotification from '@components/layout/FallbackNotification';
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/dist/katex.min.css';

const WORKER_BASE = import.meta.env.VITE_WORKER_URL || 'https://palinos-ai-proxy.gnaneshbalusa016g.workers.dev';

/**
 * After each AI response, extract key facts and append them to the user's
 * brain doc (the "single file" infinite memory). Silently fails — never
 * interrupts the conversation.
 */
// After each AI response: extract structured memories, save per-conversation to Convex,
// and append to the user's cross-conversation brain doc.
async function updateBrainDocInBackground(userContent, assistantContent, currentDoc, userId, convId, upsertFn, createMemoryFn) {
  try {
    const resp = await fetch(`${WORKER_BASE}/api/memorize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: [
          { role: 'user', content: userContent.slice(0, 800) },
          { role: 'assistant', content: assistantContent.slice(0, 800) },
        ],
      }),
    });
    if (!resp.ok) return;
    const data = await resp.json();

    // Save each extracted memory — immediately to local store AND to Convex
    const mems = Array.isArray(data.memories) ? data.memories : [];
    if (mems.length > 0) {
      const isRealConvId = convId && !String(convId).startsWith('conv_');
      const { addMemory: addMem } = useAppStore.getState();
      for (const m of mems) {
        if (!m.content || m.content.length < 5) continue;
        const newMem = {
          id: uid(),
          content: m.content,
          category: m.category || 'fact',
          conversationId: isRealConvId ? convId : undefined,
          createdAt: Date.now(),
        };
        addMem(newMem); // instant local display — no Convex round-trip needed
        if (createMemoryFn) {
          createMemoryFn({
            userId,
            conversationId: isRealConvId ? convId : undefined,
            content: m.content,
            category: m.category || 'fact',
          }).catch(() => {});
        }
      }
    }

    const facts = data.facts || mems.map((m) => `- ${m.content}`).join('\n');
    if (!facts || !facts.trim()) return;

    const today = new Date().toISOString().slice(0, 10);
    let newDoc = (currentDoc || '# Palinos Memory Archive\n') + `\n### ${today}\n${facts}`;

    if (newDoc.length > 8000) {
      const compResp = await fetch(`${WORKER_BASE}/api/compress-memory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc: newDoc }),
      });
      if (compResp.ok) {
        const { compressed } = await compResp.json();
        if (compressed) newDoc = compressed;
      }
    }

    await upsertFn({ userId, content: newDoc });
  } catch { /* silent — brain doc update never breaks chat */ }
}

// ── VS Code–style syntax highlighting ────────────────────────────
function syntaxHighlight(rawCode, lang) {
  const l = (lang || '').toLowerCase();
  const supported = ['js','jsx','ts','tsx','javascript','typescript','python','py','css','scss','html','json','bash','sh','shell','sql','go','rust','java','c','cpp','cs','csharp','rb','ruby','php','swift','kotlin','yaml','yml','toml','md','markdown'];
  if (!supported.includes(l)) return null;

  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const isPython = ['python', 'py'].includes(l);
  const isShell  = ['bash', 'sh', 'shell'].includes(l);
  const isJSON   = l === 'json';
  const isCss    = ['css', 'scss'].includes(l);
  const isHtml   = l === 'html';

  const spans = [];

  const add = (re, color) => {
    const rx = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
    let m;
    while ((m = rx.exec(rawCode)) !== null) {
      const s = m.index, e = m.index + m[0].length;
      if (!spans.some((x) => s < x.end && e > x.start)) spans.push({ start: s, end: e, color });
    }
  };

  // Comments
  if (isPython || isShell)  add(/#[^\n]*/g,         '#6a9955');
  if (!isPython && !isShell && !isJSON && !isCss && !isHtml) {
    add(/\/\/[^\n]*/g,       '#6a9955');
    add(/\/\*[\s\S]*?\*\//g, '#6a9955');
  }
  if (isCss) {
    add(/\/\*[\s\S]*?\*\//g, '#6a9955');
    add(/(#[0-9a-fA-F]{3,8})\b/g, '#ce9178');
  }
  if (isHtml) {
    add(/<!--[\s\S]*?-->/g,  '#6a9955');
    add(/<\/?[a-zA-Z][a-zA-Z0-9-]*/g, '#569cd6');
    add(/\s([a-zA-Z-]+)=/g,  '#9cdcfe');
  }

  // Strings
  if (isPython) {
    add(/"""[\s\S]*?"""/g, '#ce9178');
    add(/'''[\s\S]*?'''/g, '#ce9178');
  }
  add(/`[^`]*`/g,              '#ce9178');
  add(/"(?:[^"\\]|\\.)*"/g,    '#ce9178');
  add(/'(?:[^'\\]|\\.)*'/g,    '#ce9178');

  // Numbers
  add(/\b(0x[0-9a-fA-F]+|\d+\.?\d*([eE][+-]?\d+)?)\b/g, '#b5cea8');

  // Keywords
  if (isPython) {
    add(/\b(def|class|import|from|return|if|elif|else|for|while|try|except|finally|with|as|in|not|and|or|is|lambda|pass|break|continue|raise|yield|del|global|nonlocal|assert|True|False|None|async|await|print)\b/g, '#569cd6');
  } else if (!isJSON && !isCss && !isHtml) {
    add(/\b(const|let|var|function|return|if|else|for|while|do|class|import|export|default|from|new|this|typeof|instanceof|async|await|try|catch|finally|throw|null|undefined|true|false|break|continue|switch|case|in|of|extends|super|static|get|set|yield|delete|void|interface|type|enum|implements|public|private|protected|readonly|abstract|namespace|require|module|struct|fn|let|mut|impl|trait|use|pub|self|match|where|select|from|where|join|on|group|order|by|insert|into|update|set|delete|create|table|index|drop|alter)\b/g, '#569cd6');
  }

  // Built-ins / globals
  if (!isJSON && !isCss && !isHtml) {
    add(/\b(console|Math|Date|Array|Object|String|Number|Boolean|Promise|Error|JSON|parseInt|parseFloat|isNaN|setTimeout|setInterval|fetch|window|document|process|Buffer|Symbol|Map|Set|WeakMap|WeakSet|Proxy|Reflect|eval|len|range|list|dict|tuple|print|input|open|enumerate|zip|map|filter|sorted)\b/g, '#4ec9b0');
  }

  // Function/method calls: word followed by (
  if (!isJSON && !isCss && !isHtml) {
    add(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?=\()/g, '#dcdcaa');
  }

  // CSS property names
  if (isCss) {
    add(/([a-z-]+)\s*:/g, '#9cdcfe');
  }

  // JSON keys
  if (isJSON) {
    add(/"([^"]+)"\s*:/g, '#9cdcfe');
  }

  spans.sort((a, b) => a.start - b.start);

  let html = '';
  let pos = 0;
  for (const sp of spans) {
    if (sp.start >= pos) {
      if (sp.start > pos) html += escape(rawCode.slice(pos, sp.start));
      html += `<span style="color:${sp.color}">${escape(rawCode.slice(sp.start, sp.end))}</span>`;
      pos = sp.end;
    }
  }
  if (pos < rawCode.length) html += escape(rawCode.slice(pos));
  return html;
}

// Trim CloudCall message history to stay under the CF AI context window (80k tokens ≈ 320k chars)
function trimForCloudCall(messages, maxChars = 180000) {
  if (!messages.length) return messages;
  const system = messages[0];
  const history = messages.slice(1);
  let chars = (system.content || '').length;
  const kept = [];
  for (let i = history.length - 1; i >= 0; i--) {
    const c = (history[i].content || '').length;
    if (chars + c > maxChars) break;
    chars += c;
    kept.unshift(history[i]);
  }
  return [system, ...kept];
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-0.5 text-[11px] rounded bg-[#3e3f42] text-[#8f9196] hover:text-white border border-[#515153]">
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ── Image lightbox with zoom + canvas annotation ─────────────────────────────
function ImageLightbox({ src, alt, onClose, onAnnotated }) {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [tool, setTool] = useState('rect');
  const [color, setColor] = useState('#ff3b30');
  const [shapes, setShapes] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);

  // Redraw canvas whenever shapes / in-progress shape changes
  useEffect(() => {
    if (!canvasRef.current || !imgSize.w) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, imgSize.w, imgSize.h);
    [...shapes, ...(drawing ? [drawing] : [])].forEach((s) => drawShape(ctx, s));
  }, [shapes, drawing, imgSize]);

  function drawShape(ctx, s) {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (s.type === 'rect') {
      ctx.strokeRect(s.x, s.y, s.w, s.h);
    } else if (s.type === 'circle') {
      ctx.beginPath();
      ctx.ellipse(s.x + s.w / 2, s.y + s.h / 2, Math.abs(s.w / 2), Math.abs(s.h / 2), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.type === 'arrow') {
      const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
      const angle = Math.atan2(dy, dx);
      const headLen = Math.max(12, Math.min(24, Math.sqrt(dx * dx + dy * dy) * 0.25));
      ctx.beginPath();
      ctx.moveTo(s.x1, s.y1);
      ctx.lineTo(s.x2, s.y2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(s.x2, s.y2);
      ctx.lineTo(s.x2 - headLen * Math.cos(angle - Math.PI / 6), s.y2 - headLen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(s.x2, s.y2);
      ctx.lineTo(s.x2 - headLen * Math.cos(angle + Math.PI / 6), s.y2 - headLen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    }
  }

  function canvasPos(e) {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * imgSize.w / r.width, y: (e.clientY - r.top) * imgSize.h / r.height };
  }

  function onMouseDown(e) {
    e.preventDefault();
    const p = canvasPos(e);
    setDrawing(tool === 'arrow'
      ? { type: 'arrow', x1: p.x, y1: p.y, x2: p.x, y2: p.y, color }
      : { type: tool, x: p.x, y: p.y, w: 0, h: 0, color });
  }

  function onMouseMove(e) {
    if (!drawing) return;
    const p = canvasPos(e);
    setDrawing(drawing.type === 'arrow'
      ? { ...drawing, x2: p.x, y2: p.y }
      : { ...drawing, w: p.x - drawing.x, h: p.y - drawing.y });
  }

  function onMouseUp() {
    if (!drawing) return;
    const valid = drawing.type === 'arrow'
      ? Math.hypot(drawing.x2 - drawing.x1, drawing.y2 - drawing.y1) > 8
      : Math.abs(drawing.w) > 8 || Math.abs(drawing.h) > 8;
    if (valid) setShapes((s) => [...s, drawing]);
    setDrawing(null);
  }

  function handleSendToAI() {
    const off = document.createElement('canvas');
    off.width = imgSize.w; off.height = imgSize.h;
    const ctx = off.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      [...shapes].forEach((s) => drawShape(ctx, s));
      onAnnotated(off.toDataURL('image/png'));
      onClose();
    };
    img.crossOrigin = 'anonymous';
    img.src = src;
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const TOOLS = [{ id: 'rect', label: '□ Box' }, { id: 'circle', label: '○ Circle' }, { id: 'arrow', label: '↗ Arrow' }];
  const COLORS = ['#ff3b30', '#ff9500', '#ffd60a', '#34c759', '#0a84ff', '#ffffff'];

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(0,0,0,0.93)' }} onMouseUp={onMouseUp}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 flex-shrink-0 flex-wrap" style={{ background: '#1d1e22', borderBottom: '1px solid #515153' }}>
        {/* Zoom */}
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))} className="w-7 h-7 rounded text-[14px] font-bold bg-[#323335] border border-[#515153] text-[#d5d6d9] hover:border-[#6498CC] flex items-center justify-center">+</button>
          <span className="text-[11px] text-[#8f9196] w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.max(0.2, +(z - 0.25).toFixed(2)))} className="w-7 h-7 rounded text-[15px] font-bold bg-[#323335] border border-[#515153] text-[#d5d6d9] hover:border-[#6498CC] flex items-center justify-center">−</button>
          <button onClick={() => setZoom(1)} className="px-2 h-7 rounded text-[10px] bg-[#323335] border border-[#515153] text-[#8f9196] hover:text-white ml-0.5">Fit</button>
        </div>
        <div className="w-px h-5 bg-[#515153]" />
        {/* Tools */}
        {TOOLS.map((t) => (
          <button key={t.id} onClick={() => setTool(t.id)}
            className={`px-2.5 h-7 rounded text-[11px] font-medium border transition-colors ${tool === t.id ? 'bg-[#6498CC] border-[#6498CC] text-white' : 'bg-[#323335] border-[#515153] text-[#d5d6d9] hover:border-[#6498CC]'}`}>
            {t.label}
          </button>
        ))}
        <div className="w-px h-5 bg-[#515153]" />
        {/* Colors */}
        {COLORS.map((c) => (
          <button key={c} onClick={() => setColor(c)}
            className={`w-5 h-5 rounded-full border-2 transition-all flex-shrink-0 ${color === c ? 'border-white scale-125' : 'border-transparent hover:border-white/50'}`}
            style={{ background: c }} />
        ))}
        <div className="w-px h-5 bg-[#515153]" />
        <button onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length}
          className="px-2 h-7 rounded text-[11px] bg-[#323335] border border-[#515153] text-[#8f9196] hover:text-white disabled:opacity-30">↩ Undo</button>
        <button onClick={() => setShapes([])} disabled={!shapes.length}
          className="px-2 h-7 rounded text-[11px] bg-[#323335] border border-[#515153] text-[#8f9196] hover:text-[#e17171] disabled:opacity-30">✕ Clear</button>
        <div className="flex-1" />
        {shapes.length > 0 && (
          <button onClick={handleSendToAI}
            className="flex items-center gap-1.5 px-3 h-7 rounded text-[12px] font-bold text-white"
            style={{ background: '#F6661E' }}>
            Ask AI about this →
          </button>
        )}
        <button onClick={onClose} className="px-2 h-7 rounded text-[11px] bg-[#323335] border border-[#515153] text-[#8f9196] hover:text-white">✕ Close</button>
      </div>

      {/* Image + canvas */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-6">
        <div style={{ position: 'relative', display: 'inline-block', transform: `scale(${zoom})`, transformOrigin: 'center center', transition: 'transform 0.12s' }}>
          <img src={src} alt={alt || ''} style={{ display: 'block', maxWidth: '80vw', maxHeight: '75vh' }} draggable={false} />
          {imgSize.w > 0 && (
            <canvas ref={canvasRef} width={imgSize.w} height={imgSize.h}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'crosshair' }}
              onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} />
          )}
        </div>
      </div>
      <div className="py-2 text-center text-[11px] flex-shrink-0" style={{ color: '#515153' }}>
        Draw shapes to highlight areas → click <span style={{ color: '#F6661E', fontWeight: 600 }}>Ask AI about this</span> to send the annotated image
      </div>
    </div>
  );
}

function ImageBlock({ alt, src, onAnnotated }) {
  const [loaded, setLoaded] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const isDataUrl = src.startsWith('data:');

  const handleDownload = (e) => {
    e.stopPropagation();
    const a = document.createElement('a');
    a.href = src; a.download = alt || 'image.png';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <>
      <div className="relative group my-3 inline-block max-w-full cursor-zoom-in" onClick={() => loaded && setLightbox(true)}>
        <img
          src={src} alt={alt || 'image'}
          onLoad={() => setLoaded(true)}
          className="max-w-full rounded-lg border border-[#515153] block"
          style={{ maxHeight: '420px' }}
          draggable={false}
        />
        {loaded && (
          <div className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <span className="text-white text-[12px] font-medium bg-black/60 px-3 py-1.5 rounded-full">🔍 Click to zoom & annotate</span>
          </div>
        )}
        {loaded && (
          <button onClick={handleDownload}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-[11px] rounded bg-[#232426]/90 text-[#8f9196] hover:text-white border border-[#515153]">
            ↓ Save
          </button>
        )}
        {alt && loaded && <div className="text-[11px] text-[#515153] mt-1 text-center">{alt}</div>}
      </div>
      {lightbox && (
        <ImageLightbox src={src} alt={alt} onClose={() => setLightbox(false)} onAnnotated={onAnnotated ?? (() => {})} />
      )}
    </>
  );
}

// ── Thinking / reasoning block (DeepSeek <think>, Claude extended thinking) ──
function ThinkingBlock({ content }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-2 rounded-lg border border-[#3e3f42] overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#2b2c2f] hover:bg-[#323335] transition-colors text-left"
      >
        <svg className={`w-3.5 h-3.5 text-[#8f9196] flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        <span className="text-[11px] text-[#8f9196] font-medium">Reasoning</span>
        <span className="text-[10px] text-[#515153] ml-auto">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <pre className="px-3 py-2 text-[12px] text-[#8f9196] font-mono whitespace-pre-wrap leading-relaxed bg-[#1d1e22] overflow-x-auto" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {content.trim()}
        </pre>
      )}
    </div>
  );
}

// ── Mermaid diagram (lazy-loaded) ─────────────────────────────────────────────
function MermaidBlock({ code }) {
  const ref = useRef(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({ startOnLoad: false, theme: 'dark', themeVariables: { background: '#1d1e22', primaryColor: '#6498CC', lineColor: '#515153', textColor: '#d5d6d9' } });
      const id = 'mmd-' + Math.random().toString(36).slice(2);
      mermaid.render(id, code).then(({ svg }) => {
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      }).catch((e) => {
        if (!cancelled) setError(String(e.message || e));
      });
    }).catch(() => setError('Mermaid not available'));
    return () => { cancelled = true; };
  }, [code]);
  if (error) return <pre className="text-[#e17171] text-[12px] font-mono p-3 bg-[#1d1e22] rounded border border-[#7a3535]">{error}</pre>;
  return <div ref={ref} className="my-3 overflow-x-auto flex justify-center" style={{ minHeight: 60 }} />;
}

// ── KaTeX math inline rendering ───────────────────────────────────────────────
function MathRenderer({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      try {
        renderMathInElement(ref.current, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true },
          ],
          throwOnError: false,
        });
      } catch {}
    }
  }, [children]);
  return <span ref={ref}>{children}</span>;
}

// Parse a pipe-table block into { headers, rows } or null if not a valid table
function parseTable(block) {
  const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;
  if (!lines[0].startsWith('|') || !lines[1].match(/^\|[\s\-:|]+\|/)) return null;
  const splitCells = (line) => line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const headers = splitCells(lines[0]);
  const rows = lines.slice(2).filter((l) => l.startsWith('|')).map(splitCells);
  return { headers, rows };
}

function MarkdownContent({ content, onAnnotated }) {
  // Strip <route> tag — model name shown above bubble
  let cleanContent = content.replace(/<route>.*?<\/route>/s, '').trimEnd();

  // Extract and render <think>/<thinking> blocks before other processing
  const thinkBlocks = [];
  cleanContent = cleanContent.replace(/<think(?:ing)?>([\s\S]*?)<\/think(?:ing)?>/gi, (_, inner) => {
    const idx = thinkBlocks.length;
    thinkBlocks.push(inner);
    return `\x00think:${idx}\x00`;
  });

  // Split on fenced code blocks first, then process remaining text blocks
  const parts = cleanContent.split(/(```[\s\S]*?```)/g);
  return (
    <div className="chat-markdown text-[14px] leading-relaxed space-y-2">
      {parts.map((part, i) => {
        if (part.startsWith('```')) {
          const lines = part.split('\n');
          const lang = lines[0].replace('```', '').trim();
          if (lang === 'tool') return null;
          const code = lines.slice(1, lines[lines.length - 1].trim() === '```' ? -1 : undefined).join('\n');
          if (lang === 'mermaid') return <MermaidBlock key={i} code={code} />;
          const highlighted = syntaxHighlight(code, lang);
          return (
            <div key={i} className="relative group my-3 rounded-[6px] overflow-hidden border border-[#3e3f42]">
              <div className="flex items-center justify-between bg-[#252526] px-3 py-1.5 border-b border-[#3e3f42]">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                    <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                    <span className="w-3 h-3 rounded-full bg-[#27c93f]" />
                  </div>
                  <span className="text-[#858585] text-[11px] font-mono ml-1">{lang || 'code'}</span>
                </div>
                <CopyButton text={code} />
              </div>
              <pre className="bg-[#1e1e1e] p-4 overflow-x-auto m-0">
                {highlighted
                  ? <code className="text-[13px] font-mono leading-[1.6] text-[#d4d4d4]" dangerouslySetInnerHTML={{ __html: highlighted }} />
                  : <code className="text-[13px] font-mono leading-[1.6] text-[#d4d4d4]">{code}</code>
                }
              </pre>
            </div>
          );
        }
        // Process inline markdown in text blocks
        return part.split(/\n\n+/).filter(Boolean).map((block, j) => {
          // Thinking block placeholder
          const thinkMatch = block.trim().match(/^\x00think:(\d+)\x00$/);
          if (thinkMatch) return <ThinkingBlock key={`${i}-${j}`} content={thinkBlocks[+thinkMatch[1]]} />;

          // Standalone image
          const imgOnlyMatch = block.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
          if (imgOnlyMatch) {
            return <ImageBlock key={`${i}-${j}`} alt={imgOnlyMatch[1]} src={imgOnlyMatch[2]} onAnnotated={onAnnotated} />;
          }
          // Pipe table — render as real <table>
          const tableData = parseTable(block);
          if (tableData) {
            return (
              <div key={`${i}-${j}`} className="overflow-x-auto my-3 rounded-lg border border-[#515153]">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr style={{ background: '#2b2c2f' }}>
                      {tableData.headers.map((h, k) => (
                        <th key={k} className="px-3 py-2 text-left font-semibold text-[#d5d6d9] border-b border-[#515153] whitespace-nowrap"
                          dangerouslySetInnerHTML={{ __html: inlineHtml(h) }} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.rows.map((row, k) => (
                      <tr key={k} style={{ background: k % 2 === 0 ? '#232426' : '#272829' }} className="hover:bg-[#2b2c2f] transition-colors">
                        {row.map((cell, m) => (
                          <td key={m} className="px-3 py-2 text-[#d5d6d9] border-b border-[#3e3f42]"
                            dangerouslySetInnerHTML={{ __html: inlineHtml(cell) }} />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          // Headings
          if (/^#{1,3}\s/.test(block)) {
            const level = block.match(/^(#{1,3})\s/)[1].length;
            const text = block.replace(/^#{1,3}\s/, '');
            const cls = level === 1 ? 'text-white font-bold text-[16px] mt-2' : level === 2 ? 'text-white font-bold text-[15px] mt-1' : 'text-white font-semibold text-[14px]';
            return <p key={`${i}-${j}`} className={cls} dangerouslySetInnerHTML={{ __html: inlineHtml(text) }} />;
          }
          // Bullet lists
          if (/^[-*•]\s/.test(block) || /^\d+\.\s/.test(block)) {
            const items = block.split('\n').filter((l) => /^([-*•]|\d+\.)\s/.test(l.trim()));
            return (
              <ul key={`${i}-${j}`} className="list-none space-y-1 my-1">
                {items.map((item, k) => {
                  const text = item.replace(/^([-*•]|\d+\.)\s/, '');
                  return <li key={k} className="flex gap-2 items-start"><span className="text-[#6498CC] mt-0.5 flex-shrink-0">•</span><span dangerouslySetInnerHTML={{ __html: inlineHtml(text) }} /></li>;
                })}
              </ul>
            );
          }
          // Paragraphs that may contain inline images
          if (block.includes('![')) {
            return <InlineWithImages key={`${i}-${j}`} text={block} onAnnotated={onAnnotated} />;
          }
          const hasMath = /\$/.test(block) || /\\[\[\(]/.test(block);
          if (hasMath) return <MathRenderer key={`${i}-${j}`}>{block}</MathRenderer>;
          return <p key={`${i}-${j}`} dangerouslySetInnerHTML={{ __html: inlineHtml(block.replace(/\n/g, '<br/>')) }} />;
        });
      })}
    </div>
  );
}

// Renders a text block that may contain inline images mixed with text
function InlineWithImages({ text, onAnnotated }) {
  const imgRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let match;
  while ((match = imgRegex.exec(text)) !== null) {
    if (match.index > last) parts.push({ type: 'text', value: text.slice(last, match.index) });
    parts.push({ type: 'image', alt: match[1], src: match[2] });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  return (
    <div>
      {parts.map((p, i) =>
        p.type === 'image'
          ? <ImageBlock key={i} alt={p.alt} src={p.src} onAnnotated={onAnnotated} />
          : <span key={i} dangerouslySetInnerHTML={{ __html: inlineHtml(p.value.replace(/\n/g, '<br/>')) }} />
      )}
    </div>
  );
}

function inlineHtml(text) {
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong class="text-white font-bold"><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-bold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="text-[#c5c6ca] italic">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-[#1d1e22] text-[#71e184] px-1.5 py-0.5 rounded text-[12px] font-mono">$1</code>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-[#6498CC] underline hover:text-white">$1</a>');
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <div className="w-2 h-2 rounded-full bg-[#6498CC] typing-dot" />
      <div className="w-2 h-2 rounded-full bg-[#6498CC] typing-dot" />
      <div className="w-2 h-2 rounded-full bg-[#6498CC] typing-dot" />
    </div>
  );
}

function ToolCallBadges({ content }) {
  const { parseToolCalls: _unused, ..._ } = {};
  // Lazy parse only when content is available
  if (!content) return null;
  const toolBlocks = [];
  const regex = /```tool\s*([\s\S]*?)```/g;
  let match;
  while ((match = regex.exec(content)) !== null) {
    try {
      const call = JSON.parse(match[1].trim());
      toolBlocks.push(call);
    } catch { /* skip */ }
  }
  if (toolBlocks.length === 0) return null;

  const badgeInfo = (call) => {
    switch (call.tool) {
      case 'schedule':   return { icon: '📅', label: `Added: ${call.title}`, color: 'bg-[#1e3a5f] border-[#6498CC] text-[#6498CC]' };
      case 'remove_event': return { icon: '🗑️', label: `Removed: ${call.title}`, color: 'bg-[#2d1414] border-[#7a3535] text-[#e17171]' };
      case 'update_event': return { icon: '✏️', label: `Updated: ${call.title}`, color: 'bg-[#1e3a1e] border-[#4a7a4a] text-[#71e184]' };
      case 'move_date_events': return { icon: '📦', label: `Moved: ${call.fromDate} → ${call.toDate}`, color: 'bg-[#1e2a3a] border-[#4a6a9a] text-[#6498CC]' };
      case 'memory':    return { icon: '🧠', label: `Memory: ${(call.content || '').slice(0, 40)}`, color: 'bg-[#2a1e3a] border-[#7a4a9a] text-[#b07ae8]' };
      case 'artifact':  return { icon: '📝', label: `Artifact: ${call.title}`, color: 'bg-[#1e2a1e] border-[#4a6a4a] text-[#71e184]' };
      default:          return { icon: '⚙️', label: `Tool: ${call.tool}`, color: 'bg-[#2b2c2f] border-[#515153] text-[#8f9196]' };
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-[#3e3f42]">
      {toolBlocks.map((call, i) => {
        const { icon, label, color } = badgeInfo(call);
        return (
          <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${color}`}>
            <span>{icon}</span>
            <span className="truncate max-w-[200px]">{label}</span>
          </span>
        );
      })}
    </div>
  );
}

function AttachmentChip({ file, onRemove }) {
  const typeColors = {
    pdf: { bg: 'bg-[#4c2424]', text: 'text-[#e17171]' },
    text: { bg: 'bg-[#323335]', text: 'text-[#d5d6d9]' },
    md: { bg: 'bg-[#244c2a]', text: 'text-[#71e184]' },
    image: { bg: 'bg-[#1e3a5f]', text: 'text-[#6498CC]' },
  };
  const c = typeColors[file.type] || typeColors.text;
  return (
    <div className={`flex items-center gap-2 ${c.bg} border border-[#515153] rounded-md px-2.5 py-1.5 text-[12px]`}>
      {file.dataUrl && <img src={file.dataUrl} alt="" className="w-6 h-6 object-cover rounded flex-shrink-0" />}
      {!file.dataUrl && <span className={`${c.text} font-bold uppercase text-[10px]`}>{file.type}</span>}
      <span className="text-[#d5d6d9] truncate max-w-[120px]">{file.name}</span>
      {onRemove && (
        <button onClick={onRemove} className="text-[#8f9196] hover:text-[#e17171] ml-1">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      )}
    </div>
  );
}

function MessageTimestamp({ ts }) {
  if (!ts) return null;
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const label = isToday
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return <span className="text-[10px] text-[#515153] mt-1 block select-none">{label}</span>;
}

function MsgCopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      title="Copy message"
      className="p-1 rounded text-[#515153] hover:text-[#d5d6d9] hover:bg-[#3e3f42] transition-colors"
    >
      {copied
        ? <svg className="w-3.5 h-3.5 text-[#71e184]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      }
    </button>
  );
}

export default function ChatView() {
  const {
    currentModel, setCurrentModel, availableModels,
    messages, isStreaming, setStreaming,
    memories, calendarEvents, contextFiles, tools, userName, userId,
    apiKeys,
    addMemory, addCalendarEvent, removeCalendarEventByTitle, updateCalendarEventByTitle, moveCalendarEventsByDate,
    setArtifacts, artifacts,
    activeConversationId, addMessage, addConversationMessage, loadConversationMessages,
    showFallbackNotification,
    chatMode, webgpuModelId,
    cloudcallModelId,
    customSystemPrompt, setCustomSystemPrompt,
    conversations, renameConversation, createConversation,
  } = useAppStore();

  // Brain doc — the "single file" infinite memory (one per user, read/write every conversation)
  const { brainDocContent: brainDoc, upsertBrainDoc } = useBrainDoc();

  // Convex mutations + queries — always called unconditionally (React rules of hooks)
  const convexCreateMessage       = useMutation(api.messages.create);
  const convexCreateMemory        = useMutation(api.memories.create);
  const convexCreateCalendarEvent = useMutation(api.calendar.create);
  const convexUpdateConversation  = useMutation(api.conversations.update);
  // Guard: local IDs (conv_...) are not valid Convex IDs — skip the query to avoid validator errors
  const isConvexId = (id) => Boolean(id) && !String(id).startsWith('conv_');
  const convexMessages = useQuery(
    api.messages.listByConversation,
    isConvexEnabled && isConvexId(activeConversationId) ? { conversationId: activeConversationId } : 'skip'
  );

  // Sync Convex messages → Zustand.
  // Guard: never let Convex overwrite local state with fewer messages than we already have.
  useEffect(() => {
    if (!convexMessages || !activeConversationId) return;
    if (isStreaming) return;
    if (convexMessages.length < messages.length) return;
    const formatted = convexMessages.map((m) => ({ ...m, id: m._id }));
    loadConversationMessages(activeConversationId, formatted);
  }, [convexMessages, activeConversationId, isStreaming, messages.length]);

  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const abortControllerRef = useRef(null);

  // Edit message state
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingMsgText, setEditingMsgText] = useState('');

  // Drag-and-drop state
  const [isDragging, setIsDragging] = useState(false);

  // Called when user annotates an image in the lightbox and clicks "Ask AI about this"
  const handleAnnotatedImage = useCallback((dataUrl) => {
    setAttachments((prev) => [...prev, {
      id: uid(), name: 'annotated.png', type: 'image',
      dataUrl, base64: dataUrl.split(',')[1], mimeType: 'image/png',
    }]);
    setInput((v) => v || 'What do you see in the highlighted area?');
  }, []);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showFileTypes, setShowFileTypes] = useState(false);
  const [showSysPrompt, setShowSysPrompt] = useState(false);
  const [editingSysPrompt, setEditingSysPrompt] = useState(false);
  const [sysPromptDraft, setSysPromptDraft] = useState('');

  // Build system prompt for ALL modes — includes brain doc + memories + context
  const buildFullPrompt = useCallback((mode, extraContext = '') => {
    const base = customSystemPrompt
      ? customSystemPrompt + (extraContext ? `\n\n${extraContext}` : '')
      : buildSystemPrompt({
          systemPrompt: undefined,
          memories,
          contextFiles,
          userName,
          tools: tools ?? [],
          brainDoc,
          mode,
        }) + (extraContext ? `\n\n${extraContext}` : '');
    return base;
  }, [customSystemPrompt, memories, contextFiles, userName, tools, brainDoc]);

  const effectiveSysPrompt = buildFullPrompt(chatMode);
  const [streamingContent, setStreamingContent] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTranscript, setRecordingTranscript] = useState('');

  // WebGPU model loading state
  const [gpuModelLoaded, setGpuModelLoaded] = useState(isModelLoaded());
  const [gpuLoading, setGpuLoading] = useState(false);
  const [gpuProgress, setGpuProgress] = useState(null); // { file, progress }
  const [gpuError, setGpuError] = useState('');
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  // Streaming throttle — buffer tokens and flush at ~30 fps to avoid expensive per-token re-renders
  const streamBufRef = useRef('');
  const streamTimerRef = useRef(null);

  // Reset loaded flag when model changes so banner re-appears
  useEffect(() => {
    setGpuModelLoaded(isModelLoaded());
    setGpuProgress(null);
    setGpuError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webgpuModelId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming, streamingContent]);

  // Image paste from clipboard (Ctrl+V)
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          const reader = new FileReader();
          reader.onload = (ev) => {
            setAttachments((prev) => [...prev, {
              id: uid(), name: `image_${Date.now()}.png`, type: 'image',
              size: file.size, dataUrl: ev.target.result, file,
            }]);
          };
          reader.readAsDataURL(file);
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  // Keyboard shortcuts: Ctrl+K = new chat, Escape = stop streaming
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const { createConversation: create, chatMode: mode, currentModel: model, userId: uid2 } = useAppStore.getState();
        create({ title: 'New Conversation', model, userId: uid2, chatMode: mode });
      }
      if (e.key === 'Escape' && useAppStore.getState().isStreaming) {
        abortControllerRef.current?.abort();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Drag-and-drop file handling
  useEffect(() => {
    const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const onDragLeave = (e) => { if (!e.relatedTarget) setIsDragging(false); };
    const onDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      files.forEach((file) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (ev) => setAttachments((p) => [...p, { id: uid(), name: file.name, type: 'image', size: file.size, dataUrl: ev.target.result, file }]);
          reader.readAsDataURL(file);
        } else if (ext === 'csv' || ext === 'tsv') {
          const reader = new FileReader();
          reader.onload = (ev) => setAttachments((p) => [...p, { id: uid(), name: file.name, type: 'csv', size: file.size, textContent: ev.target.result, file }]);
          reader.readAsText(file);
        } else {
          setAttachments((p) => [...p, { id: uid(), name: file.name, type: ext === 'pdf' ? 'pdf' : 'text', size: file.size, file }]);
        }
      });
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  // Voice recording / speech-to-text
  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      let finalTranscript = input;
      recognition.onresult = (e) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript + ' ';
          else interim = e.results[i][0].transcript;
        }
        setRecordingTranscript(interim);
        setInput(finalTranscript + interim);
      };
      recognition.onerror = () => { setIsRecording(false); setRecordingTranscript(''); };
      recognition.onend = () => { setIsRecording(false); setRecordingTranscript(''); };
      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
    } else {
      alert('Speech recognition is not supported in this browser. Try Chrome or Edge.');
    }
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
    setRecordingTranscript('');
    textareaRef.current?.focus();
  };

  // Get the primary and fallback API key for the selected model's provider
  const getApiKeys = useCallback(() => {
    const modelInfo = availableModels.find((m) => m.id === currentModel);
    const provider = modelInfo?.provider ?? 'anthropic';
    const primary = apiKeys.find((k) => k.provider === provider && k.isActive && !k.isFallback);
    const fallback = apiKeys.find((k) => k.isActive && k.isFallback);
    return {
      apiKey: primary ? atob(primary.keyEncrypted) : '',
      provider,
      fallbackKey: fallback ? atob(fallback.keyEncrypted) : undefined,
      fallbackProvider: fallback?.provider,
    };
  }, [availableModels, currentModel, apiKeys]);

  const handleLoadGPU = async () => {
    if (!isWebGPUSupported()) {
      setGpuError('WebGPU is not supported. Use Chrome 113+ or Edge 113+.');
      return;
    }
    setGpuLoading(true);
    setGpuError('');
    try {
      await loadWebGPUModel(webgpuModelId, (info) => {
        if (info && (info.status === 'progress' || info.status === 'download') && info.file) {
          setGpuProgress({ file: info.file.split('/').pop(), progress: Math.round(info.progress ?? 0) });
        }
      });
      setGpuModelLoaded(true);
      setGpuProgress(null);
    } catch (err) {
      setGpuError(err.message || 'Failed to load model.');
    } finally {
      setGpuLoading(false);
    }
  };

  // Throttled stream updater — batches token updates to ~30fps so MarkdownContent
  // doesn't re-render + re-highlight on every single token
  const flushStream = useCallback((text) => {
    streamBufRef.current = text;
    if (!streamTimerRef.current) {
      streamTimerRef.current = setTimeout(() => {
        setStreamingContent(streamBufRef.current);
        streamTimerRef.current = null;
      }, 33);
    }
  }, []);

  const handleSend = async (overrides = {}) => {
    const { contentOverride, messagesForHistory } = overrides;
    const content = (contentOverride !== undefined ? contentOverride : input).trim();
    if (!content && attachments.length === 0) return;
    if (isStreaming) return;
    if (chatMode === 'webgpu' && !gpuModelLoaded) {
      setGpuError('Model not loaded — click "Load model" first.');
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const convId = activeConversationId;

    // Separate image vs non-image attachments
    const imageAttachments = attachments.filter((a) => a.type === 'image' && a.dataUrl);
    const msgAttachments = attachments.map((a) => ({ id: a.id, name: a.name, type: a.type, size: a.size }));

    const userMessage = {
      id: uid(),
      role: 'user',
      content,
      attachments: msgAttachments,
      createdAt: Date.now(),
    };

    // Show the message in UI immediately (with the attachment chips)
    if (convId) {
      addConversationMessage(convId, userMessage);
      if (isConvexEnabled && isConvexId(convId)) {
        convexCreateMessage({ conversationId: convId, role: 'user', content, attachments: msgAttachments }).catch(console.error);
      }
    } else {
      addMessage(userMessage);
    }

    setInput('');
    setAttachments([]);
    setStreaming(true);
    streamBufRef.current = '';
    setStreamingContent('');

    const { apiKey, provider, fallbackKey, fallbackProvider } = getApiKeys();

    // ── Image analysis (all modes) ────────────────────────────────
    // Describe each pasted/uploaded image via CF Workers AI llava, then inject
    // the descriptions as context so ANY model can "see" the image.
    let imageContext = '';
    if (imageAttachments.length > 0) {
      const descs = await Promise.allSettled(
        imageAttachments.map((img) =>
          analyzeImageViaCloud(img.dataUrl, content || 'Describe this image in detail.')
        )
      );
      imageContext = descs
        .map((r, i) => {
          const name = imageAttachments[i].name || `image_${i + 1}`;
          return r.status === 'fulfilled' && r.value
            ? `[Image: ${name}]\n${r.value}`
            : `[Image: ${name}] (could not analyze)`;
        })
        .join('\n\n');
    }

    // Build the effective user content for the AI (text + image descriptions)
    const aiUserContent = [imageContext, content].filter(Boolean).join('\n\n') || content;

    // historyForApi uses the enriched content for the current turn, raw content for history
    const msgsToUse = messagesForHistory ?? messages;
    const historyForApi = [
      ...msgsToUse.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user', content: aiUserContent },
    ];

    // ── URL crawling ──────────────────────────────────────────────
    const urlRegex = /https?:\/\/[^\s)>\]"']+/g;
    const urlMatches = content.match(urlRegex) ?? [];
    let crawledContext = [];
    if (urlMatches.length > 0) {
      const results = await Promise.all(urlMatches.map((u) => crawlUrl(u)));
      crawledContext = results.filter(Boolean).map((r) => ({
        name: r.title || r.url,
        type: 'link',
        content: r.text,
        url: r.url,
        wordCount: r.wordCount,
        charCount: r.text?.length ?? 0,
      }));
    }

    const crawledContextStr = crawledContext.length > 0
      ? '## Web content (from links in your message)\n' +
        crawledContext.map((c) => `### ${c.name}\n${(c.content || '').slice(0, 4000)}`).join('\n\n')
      : '';

    let fullContent = '';
    try {
      if (chatMode === 'webgpu') {
        const sysContent = buildFullPrompt('webgpu', crawledContextStr);
        const webgpuMessages = [{ role: 'system', content: sysContent }, ...historyForApi];
        await sendWebGPUMessage(webgpuMessages, (token) => {
          fullContent += token;
          flushStream(fullContent);
        });
      } else if (chatMode === 'cloudcall') {
        // Image generation request (distinct from image analysis above)
        const isImageGenRequest = imageAttachments.length === 0 && (
          /\b(generate|create|draw|make|design|produce)\s+(an?\s+)?(image|picture|photo|artwork|illustration|drawing|painting)\b/i.test(content)
          || /\b(image|picture|photo|artwork|illustration)\s+(of|showing|depicting)\b/i.test(content)
        );

        if (isImageGenRequest) {
          fullContent = '_Generating image..._';
          setStreamingContent(fullContent);
          const dataUrl = await generateCloudImage(content);
          const label = content.slice(0, 80).replace(/\n/g, ' ');
          fullContent = `Here's the generated image:\n\n![${label}](${dataUrl})`;
          setStreamingContent(fullContent);
        } else {
          const sysContent = buildFullPrompt('cloudcall', crawledContextStr);
          const ccMessages = trimForCloudCall([{ role: 'system', content: sysContent }, ...historyForApi]);
          const ccResult = await sendCloudCallMessage({
            messages: ccMessages,
            model: cloudcallModelId,
            signal: controller.signal,
            onChunk: (token) => {
              fullContent += token;
              flushStream(fullContent);
            },
          });
          fullContent = ccResult.content || fullContent;
          // Append routing info as a small footnote so user can see which model ran
          if (ccResult.routingReason) {
            fullContent = fullContent.trimEnd() + `\n\n<route>${ccResult.routingReason}</route>`;
          }
          // Ensure final content is flushed
          if (streamTimerRef.current) { clearTimeout(streamTimerRef.current); streamTimerRef.current = null; }
          setStreamingContent(fullContent);
        }
      } else {
        const result = await sendChatMessage({
          messages: historyForApi,
          model: currentModel,
          provider,
          apiKey,
          fallbackKey,
          fallbackProvider,
          systemPrompt: customSystemPrompt ?? undefined,
          userName,
          memories,
          contextFiles: [...crawledContext, ...contextFiles],
          tools: tools ?? [],
          brainDoc,
          mode: 'api',
          onChunk: (chunk) => {
            fullContent += chunk;
            flushStream(fullContent);
          },
          onFallback: (fp) => showFallbackNotification(fp),
        });
        fullContent = result.content || fullContent;
        if (streamTimerRef.current) { clearTimeout(streamTimerRef.current); streamTimerRef.current = null; }
        setStreamingContent(fullContent);
      }

      // Parse and apply tool calls from the response text
      const parsed = parseToolCalls(fullContent);

      // AI-emitted memory blocks
      parsed.memoryUpdates.forEach((m) => {
        if (m.action !== 'delete') {
          addMemory({ id: uid(), content: m.content, category: m.category, createdAt: Date.now() });
          if (isConvexEnabled) {
            convexCreateMemory({ userId, conversationId: isConvexId(convId) ? convId : undefined, content: m.content, category: m.category }).catch(console.error);
          }
        }
      });

      parsed.scheduleEvents.forEach((ev) => {
        const localId = uid();
        addCalendarEvent({ id: localId, ...ev });
        if (isConvexEnabled) {
          convexCreateCalendarEvent({
            userId,
            title: ev.title,
            startTime: ev.startTime,
            description: ev.description ?? undefined,
            allDay: ev.allDay ?? false,
          }).catch(console.error);
        }
      });

      parsed.removeEvents?.forEach((ev) => {
        removeCalendarEventByTitle(ev.title);
      });

      parsed.updateEvents?.forEach((ev) => {
        if (ev.startTime) updateCalendarEventByTitle(ev.title, { startTime: ev.startTime });
      });

      parsed.moveDateEvents?.forEach((ev) => {
        if (ev.fromDate && ev.toDate) moveCalendarEventsByDate(ev.fromDate, ev.toDate);
      });
      if (parsed.artifacts.length > 0) {
        setArtifacts([
          ...parsed.artifacts.map((a) => ({
            id: uid(),
            title: a.title || 'Untitled',
            type: a.type || 'document',
            language: a.language,
            content: a.content || '',
            version: 1,
            starred: false,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            metadata: {
              lineCount: (a.content || '').split('\n').length,
              wordCount: (a.content || '').trim().split(/\s+/).length,
            },
          })),
          ...artifacts,
        ]);
      }

      const assistantMsg = { id: uid(), role: 'assistant', content: fullContent, createdAt: Date.now(), metadata: { model: currentModel, mode: chatMode } };
      if (convId) {
        addConversationMessage(convId, assistantMsg);
        if (isConvexEnabled && isConvexId(convId)) {
          convexCreateMessage({ conversationId: convId, role: 'assistant', content: fullContent, metadata: { model: currentModel, mode: chatMode } }).catch(console.error);
        }
      } else {
        addMessage(assistantMsg);
      }

      // Auto-title: if this is the first exchange, use the user's message as the title
      if ((messagesForHistory ?? messages).length === 0 && convId) {
        const raw = content.slice(0, 60).replace(/\n/g, ' ').trim();
        const autoTitle = raw.length === 60 ? raw + '…' : raw;
        if (autoTitle) {
          renameConversation(convId, autoTitle);
          if (isConvexEnabled && isConvexId(convId)) {
            convexUpdateConversation({ id: convId, title: autoTitle }).catch(() => {});
          }
        }
      }

      // Fire-and-forget: extract memories per conversation + update brain doc.
      if (userId) {
        setTimeout(() => {
          updateBrainDocInBackground(
            content, fullContent, brainDoc, userId,
            convId,
            upsertBrainDoc,
            isConvexEnabled ? convexCreateMemory : null,
          );
        }, 0);
      }
    } catch (err) {
      if (streamTimerRef.current) { clearTimeout(streamTimerRef.current); streamTimerRef.current = null; }
      const isAbort = err?.name === 'AbortError' || err?.message === 'The user aborted a request.';
      if (isAbort) {
        // User stopped generation — save whatever was streamed
        if (fullContent) {
          const partialMsg = { id: uid(), role: 'assistant', content: fullContent, createdAt: Date.now(), metadata: { model: currentModel, mode: chatMode } };
          if (convId) addConversationMessage(convId, partialMsg);
          else addMessage(partialMsg);
        }
      } else {
        const errMsg = { id: uid(), role: 'assistant', content: err.message || String(err), isError: true, createdAt: Date.now() };
        if (convId) addConversationMessage(convId, errMsg);
        else addMessage(errMsg);
      }
    }

    abortControllerRef.current = null;
    setStreamingContent('');
    setStreaming(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const typeMap = { pdf: 'pdf', txt: 'text', md: 'md' };
    setAttachments((prev) => [...prev, { id: uid(), name: file.name, type: typeMap[ext] || 'text', size: file.size, file }]);
    e.target.value = null;
  };

  const handleRegenerate = () => {
    if (isStreaming) return;
    // Find last assistant message and the user message before it
    let asstIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') { asstIdx = i; break; }
    }
    if (asstIdx <= 0) return;
    const userMsg = messages[asstIdx - 1];
    if (!userMsg || userMsg.role !== 'user') return;
    // History = everything before the user message
    const priorMsgs = messages.slice(0, asstIdx - 1);
    if (activeConversationId) loadConversationMessages(activeConversationId, priorMsgs);
    handleSend({ contentOverride: userMsg.content, messagesForHistory: priorMsgs });
  };

  const handleEditSave = (msgId, newContent) => {
    const idx = messages.findIndex((m) => (m.id || m._id) === msgId);
    if (idx === -1) return;
    const priorMsgs = messages.slice(0, idx);
    if (activeConversationId) loadConversationMessages(activeConversationId, priorMsgs);
    setEditingMsgId(null);
    setEditingMsgText('');
    handleSend({ contentOverride: newContent, messagesForHistory: priorMsgs });
  };

  const handleExportConversation = () => {
    const title = conversations.find((c) => (c._id || c.id) === activeConversationId)?.title || 'conversation';
    const md = messages.map((m) => {
      const role = m.role === 'user' ? '**User**' : '**Palinos**';
      const ts = m.createdAt ? new Date(m.createdAt).toLocaleString() : '';
      return `${role}${ts ? ` _(${ts})_` : ''}\n\n${m.content}`;
    }).join('\n\n---\n\n');
    const blob = new Blob([`# ${title}\n\n${md}`], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${title.replace(/[^a-z0-9]/gi, '_').slice(0, 40)}.md`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const estimateTokens = () => {
    const chars = messages.reduce((sum, m) => sum + (m.content?.length ?? 0), 0);
    return Math.round(chars / 4);
  };

  const modelDisplayName = availableModels.find((m) => m.id === currentModel)?.name || 'Select Model';

  // Group models by provider for the picker
  const providerLabels = { anthropic: 'Anthropic', openai: 'OpenAI', groq: 'Groq', openrouter: 'OpenRouter', nvidia: 'NVIDIA' };
  const modelsByProvider = availableModels.reduce((acc, m) => {
    (acc[m.provider] = acc[m.provider] || []).push(m);
    return acc;
  }, {});

  // No active conversation — prompt user to create one via "+"
  if (!activeConversationId) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full text-center px-6">
        <svg className="w-14 h-14 mb-5 text-[#515153]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        <p className="text-[15px] text-[#d5d6d9] font-medium mb-2">No conversation selected</p>
        <p className="text-[13px] text-[#8f9196]">Click the <span className="text-[#6498CC] font-bold">+</span> next to Recent Chats in the sidebar to start a new conversation.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-4 md:p-6 overflow-hidden w-full h-full relative">
      <FallbackNotification />

      {/* WebGPU Status Banner */}
      {chatMode === 'webgpu' && (
        <div className="mb-3 flex-shrink-0">
          {!gpuModelLoaded && !gpuLoading && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-[#2a1e3a] border border-[#7a4a9a] rounded-lg">
              <svg className="w-4 h-4 text-[#b07ae8] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
              <div className="flex-1 min-w-0">
                <p className="text-[#b07ae8] text-[13px] font-semibold">{WEBGPU_MODELS.find(m => m.id === webgpuModelId)?.name ?? 'Local model'} · not loaded</p>
                <p className="text-[#8f9196] text-[11px]">{WEBGPU_MODELS.find(m => m.id === webgpuModelId)?.size ?? ''} · cached after first load</p>
              </div>
              <button
                onClick={handleLoadGPU}
                className="flex-shrink-0 px-3 py-1 bg-[#b07ae8] text-white text-[12px] font-semibold rounded hover:bg-[#c99af0] transition-colors"
              >
                Load model
              </button>
            </div>
          )}
          {gpuLoading && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-[#2a1e3a] border border-[#7a4a9a] rounded-lg">
              <svg className="w-4 h-4 text-[#b07ae8] animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/></svg>
              <div className="flex-1 min-w-0">
                <p className="text-[#b07ae8] text-[13px] font-semibold">Loading {WEBGPU_MODELS.find(m => m.id === webgpuModelId)?.name ?? 'model'}...</p>
                {gpuProgress && (
                  <div className="mt-1">
                    <div className="flex justify-between text-[11px] text-[#8f9196] mb-0.5">
                      <span className="truncate max-w-[200px]">{gpuProgress.file}</span>
                      <span>{gpuProgress.progress}%</span>
                    </div>
                    <div className="h-1 bg-[#3e3f42] rounded-full overflow-hidden">
                      <div className="h-full bg-[#b07ae8] rounded-full transition-all" style={{ width: `${gpuProgress.progress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {gpuModelLoaded && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#1e2a1e] border border-[#4a6a4a] rounded-lg">
              <div className="w-2 h-2 rounded-full bg-[#71e184]" />
              <span className="text-[#71e184] text-[12px] font-semibold">{WEBGPU_MODELS.find(m => m.id === webgpuModelId)?.name ?? 'Model'} loaded — running locally via WebGPU</span>
            </div>
          )}
          {gpuError && (
            <div className="mt-1 px-3 py-1.5 bg-[#2d1414] border border-[#7a3535] rounded text-[#e17171] text-[12px]">
              {gpuError}
            </div>
          )}
        </div>
      )}

      {/* System Prompt Panel */}
      {showSysPrompt && (
        <div className="mb-3 flex-shrink-0 bg-[#2b2c2f] border border-[#515153] rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#3e3f42]">
            <div className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10A10 10 0 0 1 2 12 10 10 0 0 1 12 2"/><path d="M12 16v-4M12 8h.01"/></svg>
              <span className="text-[#d5d6d9] text-[12px] font-semibold">System Prompt</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#323335] text-[#8f9196] border border-[#515153]">
                {editingSysPrompt ? 'editing' : 'read only'}
              </span>
              {customSystemPrompt && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1e3a5f] text-[#6498CC] border border-[#6498CC]">custom</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!editingSysPrompt ? (
                <button
                  onClick={() => { setSysPromptDraft(effectiveSysPrompt); setEditingSysPrompt(true); }}
                  className="px-2.5 py-1 text-[11px] font-medium text-[#6498CC] hover:text-white bg-[#323335] border border-[#515153] hover:border-[#6498CC] rounded transition-colors"
                >Edit</button>
              ) : (
                <>
                  <button
                    onClick={() => { setCustomSystemPrompt(sysPromptDraft); setEditingSysPrompt(false); }}
                    className="px-2.5 py-1 text-[11px] font-medium text-white bg-[#6498CC] hover:bg-[#5282b8] rounded transition-colors"
                  >Save</button>
                  <button
                    onClick={() => setEditingSysPrompt(false)}
                    className="px-2.5 py-1 text-[11px] font-medium text-[#8f9196] hover:text-white bg-[#323335] border border-[#515153] rounded transition-colors"
                  >Cancel</button>
                </>
              )}
              {customSystemPrompt && (
                <button
                  onClick={() => { setCustomSystemPrompt(null); setEditingSysPrompt(false); }}
                  className="px-2.5 py-1 text-[11px] font-medium text-[#e17171] hover:text-white bg-[#2d1414] border border-[#7a3535] rounded transition-colors"
                >Reset</button>
              )}
              <button onClick={() => { setShowSysPrompt(false); setEditingSysPrompt(false); }} className="text-[#8f9196] hover:text-white ml-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
          {editingSysPrompt ? (
            <textarea
              value={sysPromptDraft}
              onChange={(e) => setSysPromptDraft(e.target.value)}
              className="w-full bg-[#232426] text-[#d5d6d9] text-[12px] font-mono p-3 resize-none outline-none leading-relaxed"
              rows={10}
              spellCheck={false}
            />
          ) : (
            <pre className="text-[#d5d6d9] text-[12px] font-mono whitespace-pre-wrap leading-relaxed p-3 max-h-56 overflow-y-auto">
              {effectiveSysPrompt}
            </pre>
          )}
        </div>
      )}

      {/* Model Selector */}
      <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <div className="relative">
          {chatMode === 'webgpu' ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#2a1e3a] border border-[#7a4a9a] rounded-md text-[#b07ae8] text-[13px] font-medium select-none">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
              {WEBGPU_MODELS.find(m => m.id === webgpuModelId)?.name ?? 'Local Model'} · WebGPU · Local
            </div>
          ) : chatMode === 'cloudcall' ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1e3a2a] border border-[#4a9a6a] rounded-md text-[#71e184] text-[13px] font-medium select-none">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              {cloudcallModelId?.split('/').pop()?.replace(/-instruct.*/, '') ?? 'CF Model'} · CloudCall · Free
            </div>
          ) : (
          <button
            onClick={() => setShowModelPicker(!showModelPicker)}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#323335] border border-[#515153] rounded-md text-[#d5d6d9] hover:text-white transition-colors text-[13px] font-medium"
          >
            <span>{modelDisplayName}</span>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
          </button>
          )}
          {showModelPicker && chatMode !== 'webgpu' && (
            <div className="absolute top-full left-0 mt-1 bg-[#2b2c2f] border border-[#515153] shadow-lg rounded-[6px] z-50 py-1 w-72 max-h-[400px] overflow-y-auto">
              {Object.entries(modelsByProvider).map(([provider, models]) => (
                <div key={provider}>
                  <div className="px-3 py-1.5 text-[11px] text-[#8f9196] font-semibold uppercase tracking-wider border-b border-[#3e3f42]">
                    {providerLabels[provider] ?? provider}
                  </div>
                  {models.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setCurrentModel(m.id); setShowModelPicker(false); }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-[#3e3f42] transition-colors flex items-center justify-between ${m.id === currentModel ? 'bg-[#3e3f42]' : ''}`}
                    >
                      <div>
                        <div className="text-[#d5d6d9] text-[13px] font-medium">{m.name}</div>
                        <div className="text-[#8f9196] text-[11px] mt-0.5">{m.description}</div>
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ml-2 flex-shrink-0 ${m.free ? 'bg-[#244c2a] text-[#71e184]' : 'bg-[#1e3a5f] text-[#6498CC]'}`}>
                        {m.free ? 'FREE' : 'PAID'}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
              {availableModels.length === 0 && (
                <div className="px-4 py-3 text-[#8f9196] text-[13px]">Loading models...</div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowSysPrompt((v) => !v); setEditingSysPrompt(false); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors border ${
              showSysPrompt
                ? 'bg-[#1e3a5f] border-[#6498CC] text-[#6498CC]'
                : 'bg-[#323335] border-[#515153] text-[#8f9196] hover:border-[#6498CC] hover:text-[#d5d6d9]'
            }`}
            title="View / edit system prompt"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
            System
            {customSystemPrompt && <span className="w-1.5 h-1.5 rounded-full bg-[#F6661E] flex-shrink-0" />}
          </button>
          {messages.length > 0 && (
            <>
              <span className="text-[#515153] text-[11px]">~{estimateTokens().toLocaleString()} tk</span>
              <button
                onClick={handleExportConversation}
                title="Export conversation as Markdown"
                className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium bg-[#323335] border border-[#515153] text-[#8f9196] hover:text-white hover:border-[#6498CC] transition-colors"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto mb-4" onClick={() => { setShowModelPicker(false); setShowFileTypes(false); }}>
        {messages.length === 0 && !isStreaming ? (
          <div className="flex flex-col items-center justify-center h-full opacity-50">
            <svg className="w-12 h-12 mb-4 text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            <p className="text-[14px]">How can I help you today?</p>
            <p className="text-[12px] text-[#8f9196] mt-2">Upload PDFs, .txt or .md • Schedule events • I remember context</p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-1">
            {messages.map((msg, msgIdx) => {
              const msgId = msg.id || msg._id;
              const isLastAsst = msg.role === 'assistant' && !msg.isError && msgIdx === messages.length - 1;
              const isLastUser = msg.role === 'user' && (msgIdx === messages.length - 1 || (msgIdx === messages.length - 2 && messages[messages.length - 1]?.role === 'assistant'));
              const isEditing = editingMsgId === msgId;
              return (
              <div key={msgId} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} group/msg`}>
                <div className={`max-w-[85%] rounded-xl px-4 py-3 relative ${
                  msg.role === 'user'
                    ? 'bg-[#323335] border border-[#515153] text-[#d5d6d9]'
                    : msg.isError
                    ? 'bg-[#2d1414] border border-[#7a3535] text-[#e17171]'
                    : 'text-[#d5d6d9]'
                }`}>
                  {msg.isError && (
                    <div className="flex items-center gap-2 mb-2 text-[#e17171] text-[12px] font-semibold">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      Error
                    </div>
                  )}
                  {msg.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {msg.attachments.map((a) => <AttachmentChip key={a.id} file={a} />)}
                    </div>
                  )}
                  {msg.role === 'assistant' && !msg.isError && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="w-4 h-4 rounded-full bg-[#F6661E] flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-[9px] font-bold">P</span>
                      </div>
                      <span className="text-[11px] font-semibold text-[#8f9196]">Palinos</span>
                      {msg.metadata?.model && (
                        <span className="text-[9px] text-[#515153] px-1.5 py-0.5 rounded bg-[#2b2c2f] border border-[#3e3f42] font-mono ml-1">
                          {msg.metadata.model.split('/').pop()?.replace(/-instruct.*/, '') ?? msg.metadata.model}
                        </span>
                      )}
                    </div>
                  )}
                  {msg.role === 'user' && isEditing ? (
                    <div className="flex flex-col gap-2">
                      <textarea
                        autoFocus
                        className="w-full bg-[#232426] text-[#d5d6d9] text-[14px] rounded px-3 py-2 outline-none border border-[#6498CC] resize-none"
                        rows={Math.min(10, (editingMsgText || '').split('\n').length + 1)}
                        value={editingMsgText}
                        onChange={(e) => setEditingMsgText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave(msgId, editingMsgText); }
                          if (e.key === 'Escape') { setEditingMsgId(null); setEditingMsgText(''); }
                        }}
                      />
                      <div className="flex gap-2">
                        <button onClick={() => handleEditSave(msgId, editingMsgText)} className="px-3 py-1 text-[12px] font-semibold bg-[#6498CC] text-white rounded hover:bg-[#5282b8]">Send</button>
                        <button onClick={() => { setEditingMsgId(null); setEditingMsgText(''); }} className="px-3 py-1 text-[12px] text-[#8f9196] hover:text-white rounded border border-[#515153]">Cancel</button>
                      </div>
                    </div>
                  ) : msg.role === 'user' ? (
                    <p className="text-[14px] whitespace-pre-wrap">{msg.content}</p>
                  ) : msg.isError ? (
                    <p className="text-[13px] font-mono whitespace-pre-wrap break-all">{msg.content}</p>
                  ) : (
                    <>
                      <MarkdownContent content={msg.content} onAnnotated={handleAnnotatedImage} />
                      <ToolCallBadges content={msg.content} />
                    </>
                  )}
                  <MessageTimestamp ts={msg.createdAt} />
                  {/* Hover action bar */}
                  {!isEditing && (
                    <div className={`absolute ${msg.role === 'user' ? '-left-2 top-2 translate-x-[-100%]' : '-right-2 top-2 translate-x-[100%]'} opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-0.5 bg-[#2b2c2f] border border-[#3e3f42] rounded-lg px-1 py-1 shadow-lg`}>
                      <MsgCopyButton text={msg.content} />
                      {msg.role === 'user' && (
                        <button
                          title="Edit & re-run"
                          onClick={() => { setEditingMsgId(msgId); setEditingMsgText(msg.content); }}
                          className="p-1 rounded text-[#515153] hover:text-[#d5d6d9] hover:bg-[#3e3f42] transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                      )}
                      {isLastAsst && (
                        <button
                          title="Regenerate response"
                          onClick={handleRegenerate}
                          className="p-1 rounded text-[#515153] hover:text-[#d5d6d9] hover:bg-[#3e3f42] transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {/* Streaming preview */}
            {isStreaming && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-xl px-4 py-3 text-[#d5d6d9]">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-4 h-4 rounded-full bg-[#F6661E] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-[9px] font-bold">P</span>
                    </div>
                    <span className="text-[11px] font-semibold text-[#8f9196]">Palinos</span>
                  </div>
                  {streamingContent ? <MarkdownContent content={streamingContent} /> : <TypingIndicator />}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      {/* Attachment Preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 max-w-3xl mx-auto w-full px-1">
          {attachments.map((a) => (
            <AttachmentChip key={a.id} file={a} onRemove={() => setAttachments((prev) => prev.filter((f) => f.id !== a.id))} />
          ))}
        </div>
      )}

      {/* Input Area */}
      <div className="w-full max-w-3xl mx-auto bg-[#323335] border border-[#515153] focus-within:border-[#6498CC] rounded-xl p-3 shadow-lg flex flex-col gap-2 transition-colors flex-shrink-0">
        <textarea
          ref={textareaRef}
          className="w-full bg-transparent text-[#d5d6d9] placeholder-[#8f9196] outline-none resize-none text-[14px] min-h-[44px] max-h-[200px]"
          placeholder="Message Palinos..."
          rows="1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onInput={(e) => { e.target.style.height = ''; e.target.style.height = e.target.scrollHeight + 'px'; }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          disabled={isStreaming}
        />
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1">
            <div className="relative">
              <button
                onClick={() => setShowFileTypes(!showFileTypes)}
                className="p-1.5 text-[#8f9196] hover:text-white hover:bg-[#3e3f42] rounded-md transition-colors"
                title="Attach file"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
              </button>
              {showFileTypes && (
                <div className="absolute bottom-full left-0 mb-1 bg-[#2b2c2f] border border-[#515153] shadow-lg rounded-[6px] py-1 w-48 z-50">
                  <div className="px-3 py-1.5 text-[11px] text-[#8f9196] font-medium uppercase tracking-wider">Supported formats</div>
                  {[{ label: 'PDF Document', ext: '.pdf', icon: '📄' }, { label: 'Text File', ext: '.txt', icon: '📝' }, { label: 'Markdown', ext: '.md', icon: '📋' }].map((f) => (
                    <button
                      key={f.ext}
                      onClick={() => { fileInputRef.current.accept = f.ext; fileInputRef.current.click(); setShowFileTypes(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#3e3f42] transition-colors flex items-center gap-2 text-[13px] text-[#d5d6d9]"
                    >
                      <span>{f.icon}</span>
                      <span>{f.label}</span>
                      <span className="ml-auto text-[#8f9196] text-[11px]">{f.ext}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" className="hidden" accept={ACCEPTED_EXTENSIONS} onChange={handleFileSelect} />
            {isRecording ? (
              <button onClick={stopRecording} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#4c2424] border border-[#e17171] rounded-md text-[#e17171] text-[12px] font-medium animate-pulse">
                <span className="w-2 h-2 rounded-full bg-[#e17171] flex-shrink-0" />
                Stop
              </button>
            ) : (
              <button onClick={startRecording} className="p-1.5 text-[#8f9196] hover:text-white hover:bg-[#3e3f42] rounded-md transition-colors" title="Voice input">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
              </button>
            )}
          </div>

          {isStreaming ? (
            <button
              onClick={() => abortControllerRef.current?.abort()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2d1414] border border-[#7a3535] text-[#e17171] font-semibold text-[13px] rounded-md hover:bg-[#3a1a1a] transition-colors"
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
              Stop
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim() && attachments.length === 0}
              className="px-3 py-1.5 bg-[#d5d6d9] text-[#232426] font-semibold text-[13px] rounded-md hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
